import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { MaintenanceProvider } from './contexts/MaintenanceContext';
import { DemoProvider } from './contexts/DemoContext';
import PersonaBar from './components/demo/PersonaBar';
import DemoTour from './components/demo/DemoTour';
import DemoLandingPage from './pages/demo/DemoLandingPage';
import { isDemoMode } from './services/demoService';
import { CustomThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';

// Components
import ProtectedRoute from './utils/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/register';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Admin Pages
import AdminDashboardPage from './pages/v2/admin/DashboardPage';
import UserManagementPage from './pages/admin/NewUserManagementPage';
import NewEnrollmentManagementPage from './pages/admin/NewEnrollmentManagementPage';
import ReportsPage from './pages/admin/ReportsPage';
import PaymentManagementPage from './pages/admin/PaymentManagementPage';
import PricingManagementPage from './pages/admin/PricingManagementPage';
import ProblemFormPage from './pages/admin/ProblemFormPage';
import TenantManagementPage from './pages/admin/TenantManagementPage';
import MessageManagementPage from './pages/admin/MessageManagementPage';
import QuizBuilderPage from './pages/admin/quizzes/QuizBuilderPage';

// Admin Studio Pages
import SessionStudioPage from './pages/v2/admin/SessionStudio/SessionStudioPage';
import AchievementsStudioPage from './pages/v2/admin/AchievementsStudio/AchievementsStudioPage';
import QuizStudioPage from './pages/v2/admin/QuizStudioPage';
import TeamsStudioPage from './pages/v2/admin/TeamsStudio/TeamsStudioPage';
import ProblemPoolPage from './pages/admin/ProblemPoolPage';

// Student Pages
import StudentDashboardPage from './pages/student/DashboardPage';
import StudentDashboardPageV2 from './pages/v2/student/DashboardPage';
import AchievementsPage from './pages/student/AchievementsPage';
import AchievementsPageV2 from './pages/v2/student/AchievementsPage';
import DebugAchievements from './pages/student/achievements/DebugAchievements';
import ProblemsPage from './pages/student/ProblemsPage';
import ProblemsPageV2 from './pages/v2/student/ProblemsPage';
import ProblemsListPage from './pages/student/ProblemsListPage';
import ProblemDetailPage from './pages/student/ProblemDetailPage';
import ProblemDetailPageV2 from './pages/v2/student/ProblemDetailPage';
import QuizzesPageV2 from './pages/v2/student/QuizzesPage';
import QuizPlayPageV2 from './pages/v2/student/QuizPlayPage';
import QuizResultsPageV2 from './pages/v2/student/QuizResultsPage';
import LiveQuizPlayPage from './pages/student/live/LiveQuizPlayPage';
import RankedProfilePage from './pages/student/ranked/RankedProfilePage';
import LiveQuizControlPage from './pages/v2/admin/LiveQuizControlPage';
import LiveQuizConsolePage from './pages/admin/LiveQuizConsolePage';
import LeaderboardPage from './pages/student/LeaderboardPage';
import UserAchievementProfilePage from './pages/student/UserAchievementProfilePage';
import SessionsPage from './pages/student/SessionsPage';
import EnrollmentPage from './pages/student/EnrollmentPage';
import ComingSoonPage from './pages/student/ComingSoonPage';
import HintVideosPage from './pages/student/HintVideosPage';
import MessagesPage from './pages/student/MessagesPage';
import QuizzesPage from './pages/student/quizzes/QuizzesPage';
import QuizPlayPage from './pages/student/quizzes/QuizPlayPage';
import QuizResultsPage from './pages/student/quizzes/QuizResultsPage';
import TeamsPage from './pages/student/TeamsPage';
import TeamDetailsPage from './pages/student/TeamDetailsPage';

// Parent Pages
import ParentDashboardPage from './pages/parent/DashboardPage';
import ChildDetailPage from './pages/parent/ChildDetailPage';
import ParentEnrollmentPage from './pages/parent/EnrollmentPage';

// Teacher Pages
import TeacherDashboardPage from './pages/teacher/DashboardPage';
import TeacherAnalyticsPage from './pages/teacher/AnalyticsPage';
import StudentDetailPage from './pages/teacher/StudentDetailPage';
import TeacherRankedRosterPage from './pages/teacher/RankedRosterPage';

// Shared Pages
import ProfilePage from './pages/shared/ProfilePage';
import NotFoundPage from './pages/shared/NotFoundPage';
import LandingPage from './pages/public/LandingPage';
import AboutPage from './pages/public/AboutPage';
import FaqPage from './pages/public/FaqPage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import TermsOfServicePage from './pages/public/TermsOfServicePage';
import PaymentSuccessPage from './pages/student/PaymentSuccessPage';
import PaymentCancelPage from './pages/student/PaymentCancelPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 1 minute
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});

// Feature flag for teams
const teamsEnabled = process.env.REACT_APP_ENABLE_TEAMS === 'true';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <CustomThemeProvider>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AuthProvider>
            <NotificationProvider>
              <MaintenanceProvider>
                <Router>
                  <ErrorBoundary>
                    {/* Inert unless REACT_APP_DEMO_MODE is set, so this is a no-op off the demo
                        stack. Mounted inside the Router (not above it) because restarting the
                        demo navigates to the restored persona's default route. */}
                    <DemoProvider>
                    {/* Both render nothing until a demo sandbox has been leased. */}
                    <PersonaBar />
                    <DemoTour />
                    <Routes>
                      {/* Public routes */}
                      <Route
                        path="/"
                        element={isDemoMode() ? <DemoLandingPage /> : <LandingPage />}
                      />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />
                      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                      <Route path="/reset-password" element={<ResetPasswordPage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/faq" element={<FaqPage />} />
                      <Route path="/privacy" element={<PrivacyPolicyPage />} />
                      <Route path="/terms" element={<TermsOfServicePage />} />

                      {/* Admin routes */}
                      <Route
                        path="/admin"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <AdminDashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/users"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <UserManagementPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/users/new"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <UserManagementPage initialIsAddMode={true} />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/sessions"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <SessionStudioPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/sessions/:id"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <SessionStudioPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/enrollments"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <NewEnrollmentManagementPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/achievements"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <AchievementsStudioPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/achievements/:id"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <AchievementsStudioPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/reports"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <ReportsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/payments"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <PaymentManagementPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/payments/discount-codes"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <PaymentManagementPage initialTab={1} />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/payments/discount-codes/new"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <PaymentManagementPage initialTab={1} openDiscountDialog={true} />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/pricing"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <PricingManagementPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/problems"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <ProblemPoolPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/problems/new"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <ProblemFormPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/sessions/:sessionId/problems/new"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <ProblemFormPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/tenants"
                        element={
                          <ProtectedRoute allowedRoles={['super_admin']}>
                            <TenantManagementPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/messages"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <MessageManagementPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/quizzes"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <QuizStudioPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/quizzes/:id"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <QuizStudioPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/quizzes/new"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <QuizBuilderPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/quizzes/:id/edit"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <QuizBuilderPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/live"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <LiveQuizConsolePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/live/:roomId"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <LiveQuizControlPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/teams"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <TeamsStudioPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/teams/:id"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                            <TeamsStudioPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Student routes */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <StudentDashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <StudentDashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/v2"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <StudentDashboardPageV2 />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/achievements"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <AchievementsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/v2/achievements"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <AchievementsPageV2 />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/debug"
                        element={
                          <ProtectedRoute>
                            <DebugAchievements />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sessions"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <SessionsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sessions/:sessionId/enroll"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <EnrollmentPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Quiz routes */}
                      <Route
                        path="/student/quizzes"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <QuizzesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/v2/quizzes"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <QuizzesPageV2 />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/v2/quizzes/:id/play"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <QuizPlayPageV2 />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/v2/quizzes/:id/results"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <QuizResultsPageV2 />
                          </ProtectedRoute>
                        }
                      />
                      {/* Live quizzes are keyed by room, not quiz: a quiz may be
                          run live more than once over a season. */}
                      <Route
                        path="/student/live/:roomId"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <LiveQuizPlayPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/ranked"
                        element={
                          <ProtectedRoute allowedRoles={['student', 'admin', 'super_admin']}>
                            <RankedProfilePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/ranked/:userId"
                        element={
                          <ProtectedRoute allowedRoles={['student', 'admin', 'super_admin']}>
                            <RankedProfilePage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Dissolved into /leaderboard's Ranked tab; kept as a
                          redirect so existing links (nav history, bookmarks)
                          still land somewhere. */}
                      <Route
                        path="/student/ranked-leaderboard"
                        element={<Navigate to="/leaderboard?tab=ranked" replace />}
                      />
                      <Route
                        path="/student/quizzes/:id/play"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <QuizPlayPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/student/quizzes/:id/results"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <QuizResultsPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/problems"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <ProblemsListPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sessions/:sessionId/problems"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <ProblemsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sessions/:sessionId/problems/:problemId"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <ProblemDetailPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* V2 Student Problem Routes */}
                      <Route
                        path="/v2/sessions/:sessionId/problems"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <ProblemsPageV2 />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/v2/sessions/:sessionId/problems/:problemId"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <ProblemDetailPageV2 />
                          </ProtectedRoute>
                        }
                      />

                      {/* Session stats route */}
                      <Route
                        path="/sessions/:sessionId/stats"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <ProblemsPage showStats={true} />
                          </ProtectedRoute>
                        }
                      />

                      {/* Hint Videos routes */}
                      <Route
                        path="/hint-videos"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <HintVideosPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/hint-videos/:groupId"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <HintVideosPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Leaderboard routes */}
                      <Route
                        path="/leaderboard"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'student', 'super_admin']}>
                            <LeaderboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/leaderboard/user/:userId"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'student', 'super_admin']}>
                            <UserAchievementProfilePage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Team routes - conditionally rendered based on feature flag */}
                      {teamsEnabled && (
                        <>
                          <Route
                            path="/student/teams"
                            element={
                              <ProtectedRoute allowedRoles={['student', 'admin', 'super_admin']}>
                                <TeamsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/student/teams/:id"
                            element={
                              <ProtectedRoute allowedRoles={['student', 'admin', 'super_admin']}>
                                <TeamDetailsPage />
                              </ProtectedRoute>
                            }
                          />
                        </>
                      )}

                      {/* Messages route for students */}
                      <Route
                        path="/messages"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <MessagesPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Parent routes */}
                      <Route
                        path="/parent/dashboard"
                        element={
                          <ProtectedRoute allowedRoles={['parent']}>
                            <ParentDashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/parent/enroll"
                        element={
                          <ProtectedRoute allowedRoles={['parent']}>
                            <ParentEnrollmentPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/parent/children/:childId"
                        element={
                          <ProtectedRoute allowedRoles={['parent']}>
                            <ChildDetailPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Teacher routes */}
                      <Route
                        path="/teacher/dashboard"
                        element={
                          <ProtectedRoute allowedRoles={['teacher']}>
                            <TeacherDashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/teacher/analytics"
                        element={
                          <ProtectedRoute allowedRoles={['teacher']}>
                            <TeacherAnalyticsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/teacher/ranked"
                        element={
                          <ProtectedRoute allowedRoles={['teacher']}>
                            <TeacherRankedRosterPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/teacher/students/:studentId"
                        element={
                          <ProtectedRoute allowedRoles={['teacher']}>
                            <StudentDetailPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Shared protected routes */}
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute allowedRoles={['admin', 'student', 'parent', 'super_admin']}>
                            <ProfilePage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Maintenance mode route */}
                      <Route
                        path="/coming-soon"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <ComingSoonPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Payment routes */}
                      <Route
                        path="/payment/success"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <PaymentSuccessPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/payment/cancel"
                        element={
                          <ProtectedRoute allowedRoles={['student']}>
                            <PaymentCancelPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Utility routes */}
                      <Route path="/404" element={<NotFoundPage />} />
                      <Route path="/browse-sessions" element={<Navigate to="/sessions" replace />} />
                      <Route path="*" element={<Navigate to="/404" replace />} />
                    </Routes>
                    </DemoProvider>
                  </ErrorBoundary>
                </Router>
              </MaintenanceProvider>
            </NotificationProvider>
          </AuthProvider>
        </LocalizationProvider>
      </CustomThemeProvider>
    </QueryClientProvider>
  );
}

export default App;